import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Gift, Crown, Plane, Bell, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

interface AccountOverview {
  user_id: string;
  display_name: string | null;
  email: string;
  available_points: number;
  tier: string;
  tier_points: number;
  membership: { plan_code: string; status: string } | null;
  unread_notifications: number;
  last_booking: {
    id: string;
    title: string;
    status: string;
    created_at: string;
  } | null;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [overview, setOverview] = useState<AccountOverview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    async function fetchOverview() {
      const { data, error } = await supabase.rpc("get_account_overview", {
        p_user_id: user!.id,
      });

      if (error) {
        console.error("Error fetching overview:", error);
      } else if (data) {
        setOverview(data as unknown as AccountOverview);
      }
      setLoading(false);
    }

    fetchOverview();
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Unable to load account overview.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          Welcome back, {overview.display_name || "Member"}
        </h1>
        <p className="text-muted-foreground">Here's what's happening with your account</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Available Points</CardTitle>
            <Gift className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview.available_points.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Tier: {overview.tier}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Membership</CardTitle>
            <Crown className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold capitalize">
              {overview.membership?.plan_code || "None"}
            </div>
            <p className="text-xs text-muted-foreground">
              {overview.membership?.status || "Not enrolled"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last Booking</CardTitle>
            <Plane className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {overview.last_booking ? overview.last_booking.status : "None"}
            </div>
            <p className="text-xs text-muted-foreground">
              {overview.last_booking
                ? new Date(overview.last_booking.created_at).toLocaleDateString()
                : "No bookings yet"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Notifications</CardTitle>
            <Bell className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview.unread_notifications}</div>
            <p className="text-xs text-muted-foreground">Unread messages</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button asChild className="w-full" variant="outline">
              <Link to="/travel">
                <Plane className="mr-2 h-4 w-4" />
                Book Travel
              </Link>
            </Button>
            <Button asChild className="w-full" variant="outline">
              <Link to="/account/rewards">
                <Gift className="mr-2 h-4 w-4" />
                Redeem Points
              </Link>
            </Button>
            <Button asChild className="w-full" variant="outline">
              <Link to="/account/membership">
                <Crown className="mr-2 h-4 w-4" />
                View Membership
              </Link>
            </Button>
          </CardContent>
        </Card>

        {!overview.membership && (
          <Card className="border-primary/50">
            <CardHeader>
              <CardTitle>Join Flexaura Membership</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Get bonus points, priority support, and exclusive early access to offers — completely free!
              </p>
              <Button asChild className="w-full">
                <Link to="/account/membership">
                  Join Free <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Tier Progress</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-muted-foreground">Current Tier</span>
                <span className="font-medium capitalize">{overview.tier}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Tier Points</span>
                <span className="font-medium">{overview.tier_points.toLocaleString()}</span>
              </div>
            </div>
            <Button asChild className="w-full" variant="outline">
              <Link to="/account/points">
                View Details <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
